import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import EfImgVideo from '../ef-img-video';
import EfLink from '../ef-link';
import './index.scss';

interface EfSectionMiddleProps {
    /**
     * 资源链接是否是视频
     */
    isVideo?: boolean;
    /**
     * 所有分辨率展示的资源链接
     */
    pc?: string;
    /**
     * 分辨率小于等于1024px展示的资源链接
     */
    h5?: string;
    /**
     * 视频贴图-所有分辨率
     */
    poster?: string;
    /**
     * 视频贴图-分辨率小于等于1024px
     */
    posterH5?: string;

    /**
     * 主题色,默认 black
     */
    theme?: 'black' | 'white';

    /**
     * 是否开启进场动画
     */
    hasAnimation?: boolean;

    /**
     *
     * 进场动画是否已启动
     */
    animationEnterCallback?: () => void;

    /**
     * 文案展示位置, 默认居中
     */
    position?: 'default' | 'left' | 'right';

    /**
     * label(处于标题之上的叫label)
     */
    label?: string | React.ReactNode;
    /**
     * 主标题
     */
    title?: string;
    /**
     * 副标题
     */
    subTitle?: string;
    /**
     * 描述
     */
    desc?: string;

    /**
     * 样式名
     */
    className?: string;

    /**
     * 更多详情-链接
     */
    moreLink?: string;
    /**
     * 更多详情-文案
     */
    moreText?: string;

    /**
     * 购买按钮-链接
     */
    buyLink?: string;
    /**
     * 购买按钮-文案
     */
    buyText?: string;
}

/**
 * 维护:杨柏迪
 *
 * 段落
 */
const EfSectionMiddle: React.FC<EfSectionMiddleProps> = ({
    isVideo,
    pc,
    h5,
    poster,
    posterH5,
    theme = 'black',
    position,
    hasAnimation = true,
    animationEnterCallback,
    label,
    title,
    subTitle,
    desc,
    className,
    moreLink,
    moreText,
    buyLink,
    buyText,
}) => {
    const refSection = useRef(null);
    const [isEnter, setIsEnter] = useState(false);
    const prefixCls = `ef-section-middle`;
    const classes = classNames(prefixCls, {
        [`${className}`]: className,
        [`${prefixCls}-position-${position}`]: position,
        [`${prefixCls}-theme-${theme}`]: theme,
        [`${prefixCls}-animation`]: hasAnimation,
        [`${prefixCls}-enter`]: isEnter,
    });
    // 是否有按钮
    const hasBtn = (moreLink && moreText) || (buyLink && buyText);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (elems: any) => {
                const elem = elems[0];

                const topNum = elem.boundingClientRect.top;

                if (elem.isIntersecting && topNum > 0) {
                    setIsEnter(true);
                    animationEnterCallback && animationEnterCallback();
                }
            },
            {
                rootMargin: `0px 0px -100px 0px`,
                threshold: 0,
            },
        );
        observer.observe(refSection.current as any);
    }, []);

    return (
        <div className={classes} ref={refSection}>
            <div className={`${prefixCls}-info`}>
                <div className={`${prefixCls}-msg`}>
                    {label && <div className={`${prefixCls}-label`}>{label}</div>}
                    {title && <h2 className={`${prefixCls}-title`}>{title}</h2>}
                    {subTitle && <p className={`${prefixCls}-subTitle`}>{subTitle}</p>}
                    {desc && <div className={`${prefixCls}-desc`}>{desc}</div>}

                    {hasBtn && (
                        <div className={`${prefixCls}-btns`}>
                            {moreLink && moreText && (
                                <EfLink
                                    href={moreLink}
                                    className={`${prefixCls}-link_more`}
                                    dataLayer={[
                                        {
                                            event: 'uaEvent',
                                            event_name: 'activity_preview',
                                            button_name: moreText,
                                        },
                                    ]}
                                >
                                    {moreText}
                                </EfLink>
                            )}
                            {buyLink && buyText && (
                                <EfLink
                                    href={buyLink}
                                    className={`${prefixCls}-buy_now`}
                                    dataLayer={[
                                        {
                                            event: 'uaEvent',
                                            event_name: 'activity_preview',
                                            button_name: buyText,
                                        },
                                    ]}
                                >
                                    {buyText}
                                </EfLink>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {pc && (
                <div className={`${prefixCls}-img`}>
                    <EfImgVideo isVideo={isVideo} pc={pc} h5={h5} poster={poster} posterH5={posterH5} autoPlay={true} loop={true} />
                </div>
            )}
        </div>
    );
};

export default EfSectionMiddle;
